import { formatMoney, plural, telHref, type VisitDto } from '@kominiarz/shared';
import { Banknote, CalendarDays, MapPin, Phone, User, Wallet } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Link } from 'react-router';
import { Alert } from '../../components/Alert';
import { Button, ButtonAnchor } from '../../components/Button';
import { ColorDot } from '../../components/ColorDot';
import { EmptyState } from '../../components/EmptyState';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { PaymentDialog } from '../../components/visit/PaymentDialog';
import { visitClientName, visitPeople, visitTitle } from '../../components/VisitStatus';
import { errorMessage } from '../../lib/api';
import { local, todayLocal } from '../../lib/calendarDates';
import { useUnpaid } from '../../lib/closingApi';
import styles from './closing.module.css';

/** Done visits that still wait for the money, oldest first. */
export function UnpaidPage() {
  const query = useUnpaid();
  const [paying, setPaying] = useState<VisitDto | null>(null);
  const visits = query.data?.visits ?? [];

  return (
    <>
      <PageHeader title="Nieopłacone" subtitle="Wykonane wizyty, za które klient jeszcze nie zapłacił" />
      <PageBody>
        {query.isPending && <Spinner />}
        {query.isError && <Alert kind="error">{errorMessage(query.error)}</Alert>}
        {query.data && visits.length === 0 && (
          <EmptyState icon={Wallet} title="Wszystko opłacone">
            Gdy wykonana wizyta zostanie oznaczona jako nieopłacona, pojawi się tutaj.
          </EmptyState>
        )}
        {query.data?.totalGrosze != null && visits.length > 0 && (
          <div className={styles.total}>
            <span>Razem do zapłaty ({plural(visits.length, 'wizyta', 'wizyty', 'wizyt')})</span>
            <span className={styles.totalValue}>{formatMoney(query.data.totalGrosze)}</span>
          </div>
        )}
        {visits.length > 0 && (
          <ul className={styles.list}>
            {visits.map((v) => (
              <UnpaidItem key={v.id} visit={v} onPay={() => setPaying(v)} />
            ))}
          </ul>
        )}
      </PageBody>
      {paying && <PaymentDialog visit={paying} onClose={() => setPaying(null)} />}
    </>
  );
}

function daysAgo(iso: string): string {
  const days = Math.round(DateTime.fromISO(todayLocal()).diff(DateTime.fromISO(local(iso).toISODate()!), 'days').days);
  if (days <= 0) return 'dzisiaj';
  if (days === 1) return 'wczoraj';
  return `${plural(days, 'dzień', 'dni', 'dni')} temu`;
}

function UnpaidItem({ visit: v, onPay }: { visit: VisitDto; onPay: () => void }) {
  const client = visitClientName(v);
  return (
    <li className={styles.item}>
      <div className={styles.itemHead}>
        <h2 className={styles.itemTitle}>
          <Link to={`/wizyty/${v.id}`}>{client ?? visitTitle(v)}</Link>
        </h2>
        <span className={styles.amount}>{formatMoney(v.amountGrosze ?? 0)}</span>
      </div>
      <ul className={styles.lines}>
        <li className={styles.line}>
          <CalendarDays aria-hidden="true" />
          <span>
            {visitTitle(v)}, {local(v.startAt).toFormat('d MMMM yyyy')} ({daysAgo(v.startAt)})
          </span>
        </li>
        {v.address && (
          <li className={styles.line}>
            <MapPin aria-hidden="true" />
            <span>{v.address}</span>
          </li>
        )}
        <li className={styles.line}>
          <User aria-hidden="true" />
          <span>
            {v.assignees.map((u) => (
              <ColorDot key={u.id} color={u.color} />
            ))}{' '}
            {visitPeople(v)}
          </span>
        </li>
      </ul>
      <div className={styles.itemActions}>
        {v.phone && (
          <ButtonAnchor href={telHref(v.phone)} variant="secondary" icon={<Phone aria-hidden="true" />}>
            Zadzwoń
          </ButtonAnchor>
        )}
        {v.permissions.close && (
          <Button variant="primary" icon={<Banknote aria-hidden="true" />} onClick={onPay}>
            Opłacona
          </Button>
        )}
      </div>
    </li>
  );
}
